/* =====================================================================
   sht-prd-bundle  —  Product bundle configurator
   =====================================================================
   Responsibilities:
     - Track selected NVR / HDD / Kit (radio) and Accessories (checkbox).
     - Enforce Kit exclusivity: selecting a Kit clears NVR/HDD/Accessories;
       selecting any of those clears the Kit.
     - Recalculate bundle total on every change.
     - Switch CTA label between three states (none / DIY / kit).
     - On submit, POST a single multi-item add to /cart/add.js, then refresh
       the cart drawer using the existing sht-cart-drwr contract.
     - Expand any "+ N more" row to show its hidden options.

   Performance notes:
     - Script tag is deferred (set in section template).
     - No external deps; vanilla JS.
     - Event delegation keeps listener count to one per instance.
   ===================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('sht-prd-bundle')) return;

  // ---------- helpers ------------------------------------------------------

  function formatMoney(amountCents, moneyFormat) {
    // amountCents is the integer cents (Shopify convention).
    // moneyFormat looks like "${{amount}}" or "${{amount_no_decimals}}" etc.
    var amount = (amountCents / 100).toFixed(2);
    var fmt = moneyFormat || '${{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amountCents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(amountCents / 100).toString());
  }

  function getCartSections() {
    // Reuse the drawer's getSectionsToRender contract so we render exactly what
    // the active drawer needs. Falls back to a sensible default.
    var drawer = document.querySelector('sht-cart-drwr');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender();
    }
    return [];
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function updateHeaderCartCount(count) {
    var numEl = document.querySelector('#headerCartStatus .cart-count-number');
    if (numEl) numEl.textContent = String(count);

    var wrapEl = document.querySelector('#headerCartStatus .header__cart-count');
    if (wrapEl) {
      wrapEl.className = wrapEl.className.replace(/header-cart-count-\d+/g, '').trim() + ' header-cart-count-' + String(count);
      wrapEl.style.display = count > 0 ? '' : 'none';
    }
  }

  async function refreshCartUI() {
    var sectionsToRender = getCartSections();
    var sectionNames = Array.from(new Set(
      sectionsToRender.map(function (s) { return s.section; }).filter(Boolean).concat(['cart-icon-bubble'])
    ));

    if (sectionNames.length) {
      try {
        var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        var res = await fetch(rootUrl + 'cart?sections=' + encodeURIComponent(sectionNames.join(',')));
        if (res.ok) {
          var sections = await res.json();
          sectionsToRender.forEach(function (cfg) {
            var html = sections[cfg.section];
            if (!html || !cfg.space_selector) return;
            var doc = parseHTML(html);
            var newNode = doc.querySelector(cfg.space_selector);
            var target = document.querySelector(cfg.space_selector);
            if (!target) return;
            target.innerHTML = newNode ? newNode.innerHTML : html;
          });
        }
      } catch (e) {
        /* swallow — drawer refresh is best-effort */
      }
    }

    try {
      var rootUrl2 = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var cartRes = await fetch(rootUrl2 + 'cart.js');
      if (cartRes.ok) {
        var cart = await cartRes.json();
        updateHeaderCartCount(cart.item_count);
      }
    } catch (e) { /* noop */ }

    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  // ---------- custom element ----------------------------------------------

  class SHTProductBundle extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.baseVariantId = parseInt(this.dataset.baseVariant, 10);
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      // Bulk Savings display (attrs absent for Trade customers / feature off)
      this.volPercent = parseFloat(this.dataset.volPercent) || 0;
      this.volThresholdCents = parseInt(this.dataset.volThresholdCents, 10) || 0;

      this.totalEl = this.querySelector('.js-pbundle-total');
      this.bulkEl = this.querySelector('.js-pbundle-bulk');
      this.bulkPriceEl = this.querySelector('.js-pbundle-bulk-price');
      this.ctaEl = this.querySelector('.js-pbundle-add');
      this.ctaLabelEl = this.querySelector('.js-pbundle-cta-label');
      this.errorEl = this.querySelector('.js-pbundle-error');

      this.ctaDefault = this.ctaEl ? this.ctaEl.dataset.defaultLabel : 'Add to cart';
      this.ctaBundle = this.ctaEl ? this.ctaEl.dataset.bundleLabel : 'Add bundle to cart';
      this.ctaKit = this.ctaEl ? this.ctaEl.dataset.kitLabel : 'Add kit to cart';

      // Main product quantity input — total scales with this. The selector
      // matches Smarket's main-product section. Fall back to a generic
      // quantity input if the theme renames it.
      this.qtyInput =
        document.querySelector('.js-main-product-quantity-input') ||
        document.querySelector('sht-qty-inp input[name="quantity"]') ||
        document.querySelector('form[action*="cart/add"] input[name="quantity"]');

      // Selection state (NVR/HDD/Kit are single-select; accessories & cable
      // are managed by their own shared controllers).
      this.state = { nvr: null, hdd: null, kit: null };

      var self = this;
      var accRoot = this.querySelector('[data-accessory-panel]');
      this.accessorySelector = (accRoot && window.BundleAccessorySelector)
        ? new window.BundleAccessorySelector(accRoot, { onChange: function () { self.onAddonChange(); } })
        : null;
      var cableRoot = this.querySelector('[data-cable-panel]');
      this.cableSelector = (cableRoot && window.BundleCableSelector)
        ? new window.BundleCableSelector(cableRoot, { onChange: function () { self.onAddonChange(); } })
        : null;

      var monitorRoot = this.querySelector('[data-monitor-panel]');
      this.monitorSelector = monitorRoot && window.BundleMonitorSelector
        ? new window.BundleMonitorSelector(monitorRoot, { createChoice: function (select) { return self._createPriceChoice(select); }, onChange: function () { self.recompute(); } }) : null;

      this._mountInline();
      this._initStickyBridge();
      this.bindEvents();
      this.recompute(); // initial render in case any radios came pre-checked
    }

    // The lower builder owns selections; move its actual recorder/storage rows
    // beside the camera options so there is only one selection state.
    _mountInline() {
      if (this.dataset.cameraInline !== 'true') return;
      var slot = document.querySelector('[data-camera-inline-slot][data-product-id="' + this.dataset.productId + '"]');
      if (!slot) return;
      this._mainRoot = slot.closest('.main-product');
      this._mainForm = this._mainRoot.querySelector('form.js-main-product-form');
      this._mainButton = this._mainForm && this._mainForm.querySelector('.js-product-form-submit-btn');
      var nvr = this.querySelector('.pbundle__row[data-row="nvr"]');
      if (!this._mainButton || (!nvr && !this.monitorSelector)) return;
      this._inline = true;
      this._mainRoot.classList.add('has-inline-camera');
      this.closest('.pbundle').classList.add('pbundle--has-inline');
      this.qtyInput = this._mainForm.querySelector('[name="quantity"]') || this.qtyInput;
      this._variants = JSON.parse(this.querySelector('.js-pbundle-variants').textContent);
      var panel = document.createElement('div');
      panel.className = 'pbundle--inline';
      slot.appendChild(panel);
      this._inlinePanel = panel;
      var rows = document.createElement('div');
      rows.className = 'pbundle__rows';
      panel.appendChild(rows);
      this._inlineChoices = {};
      var self = this;
      ['nvr', 'hdd'].forEach(function (name) {
        var row = self.querySelector('.pbundle__row[data-row="' + name + '"]');
        if (!row) return;
        rows.appendChild(row);
        row.querySelector('.pbundle__row-title').textContent = name === 'nvr' ? 'NVR recorder' : 'Hard drive';
        var source = row.querySelector('.pbundle__options');
        var inputs = Array.from(source.querySelectorAll('.pbundle__opt-input'));
        var select = document.createElement('select');
        select.id = 'camera-' + name + '-' + self.dataset.sectionId;
        select.setAttribute('aria-labelledby', row.getAttribute('aria-labelledby'));
        inputs.forEach(function (input, index) {
          var option = document.createElement('option');
          var card = input.closest('.pbundle__opt');
          var label = card.querySelector('.pbundle__opt-label').textContent.trim();
          var channels = Number(input.dataset.channels) || 0;
          option.value = String(index);
          option.dataset.label = !input.value ? (name === 'nvr' ? 'No NVR' : 'No hard drive')
            : name === 'nvr' ? (channels ? channels + 'CH' : input.dataset.model || label) : label;
          option.dataset.amount = input.value ? '+' + formatMoney(Number(input.dataset.price), self.moneyFormat) : '';
          if (name === 'nvr' && input.value) {
            option.dataset.detail = input.dataset.model || label;
            if (input.dataset.includesHdd === 'true') {
              var capacity = (input.dataset.title || '').match(/\b(\d+(?:\.\d+)?)\s*TB\b/i);
              option.dataset.label += capacity ? ' · ' + capacity[1] + 'TB included' : ' · HDD included';
              option.dataset.detail = input.dataset.title || label;
            }
          }
          option.textContent = option.dataset.label + (option.dataset.amount ? ' — ' + option.dataset.amount : '') + (option.dataset.detail ? ' · ' + option.dataset.detail : '');
          option.disabled = input.disabled;
          option.selected = input.checked;
          select.appendChild(option);
        });
        source.hidden = true;
        row.appendChild(select);
        select.addEventListener('change', function () {
          var input = inputs[Number(select.value)];
          if (!input || input.disabled) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        self._inlineChoices[name] = { inputs: inputs, select: select, choice: self._createPriceChoice(select) };
      });
      // Keep one controller when the actual row moves into the buy box.
      if (this.monitorSelector) rows.appendChild(this.monitorSelector.root);
      panel.addEventListener('change', this.onChange.bind(this));
      this._inlineHelp = document.createElement('p');
      this._inlineHelp.className = 'pbundle__inline-help';
      this._inlineHelp.setAttribute('role', 'status');
      this._inlineHelp.hidden = true;
      panel.appendChild(this._inlineHelp);
      this._inlineError = document.createElement('p');
      this._inlineError.className = 'pbundle__inline-error';
      this._inlineError.setAttribute('role', 'alert');
      this._inlineError.hidden = true;
      panel.appendChild(this._inlineError);
      // Compact only the pre-made-kit shortcut, preserving its destination.
      this._mainRoot.querySelectorAll('a').forEach(function (link) {
        if (/^pre-made kits\s*→?$/i.test(link.textContent.trim())) {
          link.classList.add('smk-camera-kits-link');
          var wrapper = link.parentElement;
          if (wrapper.children.length === 1 && wrapper.tagName !== 'DIV' && wrapper.tagName !== 'P') return;
          if (wrapper.children.length === 1 && !wrapper.classList.contains('product__info')) {
            wrapper.style.padding = '0'; wrapper.style.border = '0'; wrapper.style.background = 'none'; wrapper.style.margin = '0';
          }
        }
      });
      var refresh = function () { self.recompute(); };
      this._variantObserver = new MutationObserver(function () { if (self._refreshCameraVariant()) self.recompute(); });
      this._variantObserver.observe(this._mainForm, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'disabled'] });
      this._mainRoot.addEventListener('change', function (event) {
        if (!panel.contains(event.target)) requestAnimationFrame(refresh);
      });
      this._mainRoot.addEventListener('smk:camera-selection-change', refresh);
      this._mainForm.addEventListener('click', function (event) {
        if (event.target.closest('.js-quantity-btn')) requestAnimationFrame(refresh);
      });
      document.addEventListener('click', function (event) {
        var button = event.target.closest('.js-product-form-submit-btn');
        if (!button || !(self._mainForm.contains(button) || button.closest('sht-prd-frm-sticky'))) return;
        // The native camera-only form retains its existing purchase guards.
        if (!self._hasInlineAddons()) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (!button.disabled) self.submit();
      }, true);
      document.addEventListener('submit', function (event) {
        if (event.target !== self._mainForm || !self._hasInlineAddons()) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (!self._mainButton.disabled) self.submit();
      }, true);
    }

    _rowInputs(row) {
      var selector = '.pbundle__opt-input[data-row="' + row + '"]';
      return Array.from(this.querySelectorAll(selector)).concat(this._inlinePanel ? Array.from(this._inlinePanel.querySelectorAll(selector)) : []);
    }

    _hasInlineAddons() {
      return !!(this.state.nvr || this.state.hdd || this.state.kit ||
        (this.monitorSelector && this.monitorSelector.getLines().length) ||
        (this.accessorySelector && this.accessorySelector.getLines().length) ||
        (this.cableSelector && this.cableSelector.getLines().length));
    }

    _refreshCameraVariant() {
      if (!this._inline) return false;
      var input = this._mainForm.querySelector('[name="id"]');
      var variant = input && this._variants.find(function (item) { return String(item.id) === input.value; });
      if (!variant || (this.baseVariantId === variant.id && this.basePrice === variant.price)) return false;
      this.baseVariantId = variant.id;
      this.basePrice = variant.price;
      return true;
    }

    _selectedRecorderChannels() {
      var input = this._rowInputs('nvr').find(function (item) { return item.checked && item.value; });
      return input ? Number(input.dataset.channels) || 0 : 0;
    }

    _recorderIncludesHdd() {
      var input = this._rowInputs('nvr').find(function (item) { return item.checked && item.value; });
      return !!input && input.dataset.includesHdd === 'true';
    }

    _validateInline() {
      if (!this.state.kit) {
        var id = this._mainForm.querySelector('[name="id"]');
        var variant = id && this._variants.find(function (item) { return String(item.id) === id.value; });
        if (!variant || !variant.available || id.disabled) {
          this.showError('This camera option is unavailable. Choose an available option.'); return false;
        }
        var channels = this._selectedRecorderChannels();
        if (channels && this.getQty() > channels) {
          this.showError('Choose an NVR with at least ' + this.getQty() + ' channels, or reduce the camera quantity.');
          this._inlineChoices.nvr.choice.button.focus(); return false;
        }
      }
      return true;
    }

    _syncInline(total, bulkTotal, bulkQualifies, active) {
      if (!this._inline) return;
      var self = this;
      var included = this._recorderIncludesHdd();
      this.closest('.pbundle').classList.toggle('pbundle--kit-selected', !!this.state.kit);
      var hddRow = this._inlinePanel.querySelector('.pbundle__row[data-row="hdd"]');
      if (hddRow) hddRow.hidden = included;
      Object.keys(this._inlineChoices).forEach(function (name) {
        var field = self._inlineChoices[name];
        field.select.value = String(Math.max(0, field.inputs.findIndex(function (input) { return input.checked; })));
        field.choice.sync();
      });
      var channels = this._selectedRecorderChannels();
      var over = !this.state.kit && channels && this.getQty() > channels;
      var message = over ? 'This NVR supports ' + channels + ' cameras. Choose more channels for ' + this.getQty() + ' cameras.'
        : included ? 'Hard drive included with this recorder.'
        : this.state.nvr && !this.state.hdd ? 'No hard drive selected. Supply your own compatible drive for NVR recording.'
        : this.state.hdd && !this.state.nvr ? 'Hard drive for your existing compatible recorder.' : '';
      this._inlineHelp.textContent = message;
      this._inlineHelp.hidden = !message;
      this._inlineHelp.classList.toggle('is-warning', !!over);
      this._mainRoot.classList.toggle('has-camera-addons', active);
      this._syncMainPrice(total, bulkTotal, bulkQualifies, active);
    }

    _setMainLoading(loading) {
      if (!this._inline) return;
      if (loading) this._mainDisabledBefore = this._mainButton.disabled;
      this._mainButton.disabled = loading || !!this._mainDisabledBefore;
      this._mainButton.classList.toggle('loading', loading);
      this._mainButton.setAttribute('aria-busy', String(loading));
    }

    _createPriceChoice(select) {
      var wrap = document.createElement('div');
      wrap.className = 'pbundle__choice';
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'pbundle__select pbundle__choice-trigger';
      button.id = select.id + '-trigger';
      button.disabled = select.disabled;
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      var value = document.createElement('span');
      value.className = 'pbundle__choice-line';
      value.id = select.id + '-value';
      button.setAttribute('aria-labelledby', select.getAttribute('aria-labelledby') + ' ' + value.id);
      if (select.hasAttribute('aria-describedby')) button.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
      button.appendChild(value);
      var menu = document.createElement('div');
      menu.className = 'pbundle__choice-menu';
      menu.id = select.id + '-list';
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-labelledby', select.getAttribute('aria-labelledby'));
      menu.hidden = true;
      button.setAttribute('aria-controls', menu.id);
      wrap.appendChild(button);
      wrap.appendChild(menu);
      select.after(wrap);
      select.hidden = true;
      select.classList.remove('pbundle__select');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      function appendText(parent, className, text) {
        var span = document.createElement('span');
        span.className = className;
        span.textContent = text;
        parent.appendChild(span);
      }
      function render(parent, option, detail) {
        parent.textContent = '';
        appendText(parent, 'pbundle__choice-label', option.dataset.label);
        if (option.dataset.amount) appendText(parent, 'pbundle__choice-price', option.dataset.amount);
        else if (option.dataset.included) appendText(parent, 'pbundle__choice-included', option.dataset.included);
        if (detail && option.dataset.detail) appendText(parent, 'pbundle__choice-model', option.dataset.detail);
        if (option.disabled) appendText(parent, 'pbundle__choice-model', 'Unavailable');
      }
      var options = Array.from(select.options).map(function (option) {
        var item = document.createElement('div');
        item.className = 'pbundle__choice-option';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-disabled', String(option.disabled));
        item.tabIndex = -1;
        item.dataset.value = option.value;
        render(item, option, true);
        menu.appendChild(item);
        item.addEventListener('click', function () { choose(item); });
        return item;
      });
      function sync() {
        var option = select.selectedOptions[0];
        if (!option) return;
        render(value, option, false);
        button.title = option.textContent;
        options.forEach(function (item) { item.setAttribute('aria-selected', String(item.dataset.value === select.value)); });
      }
      function close(restoreFocus) {
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        if (restoreFocus) button.focus();
      }
      function open() {
        if (button.disabled) return;
        menu.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        var item = options.find(function (el) { return el.dataset.value === select.value && el.getAttribute('aria-disabled') !== 'true'; })
          || options.find(function (el) { return el.getAttribute('aria-disabled') !== 'true'; });
        if (item) item.focus({ preventScroll: true });
      }
      function choose(item) {
        if (item.getAttribute('aria-disabled') === 'true') return;
        select.value = item.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        close(true);
      }
      button.addEventListener('click', function () { if (menu.hidden) open(); else close(false); });
      button.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); open(); }
      });
      var search = '', searchAt = 0;
      menu.addEventListener('keydown', function (event) {
        var available = options.filter(function (el) { return el.getAttribute('aria-disabled') !== 'true'; });
        var index = available.indexOf(document.activeElement);
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
        if (event.key === 'Tab') { close(true); return; }
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); if (index >= 0) choose(available[index]); return; }
        var next = null;
        if (event.key === 'ArrowDown') next = available[(index + 1) % available.length];
        if (event.key === 'ArrowUp') next = available[(index - 1 + available.length) % available.length];
        if (event.key === 'Home') next = available[0];
        if (event.key === 'End') next = available[available.length - 1];
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          search = Date.now() - searchAt > 700 ? event.key : search + event.key;
          searchAt = Date.now();
          next = available.find(function (el) { return el.textContent.toLowerCase().startsWith(search.toLowerCase()); });
        }
        if (next) { event.preventDefault(); next.focus(); }
      });
      wrap.addEventListener('focusout', function (event) { if (!wrap.contains(event.relatedTarget)) close(false); });
      document.addEventListener('pointerdown', function (event) { if (!wrap.contains(event.target)) close(false); });
      select.addEventListener('change', sync);
      sync();
      return { sync: sync, button: button };
    }

    _syncMainPrice(total, bulkTotal, bulkQualifies, hasCustom) {
      if (!this._inline) return;
      var priceArea = this._mainRoot.querySelector('.product__prices');
      var original = priceArea && priceArea.querySelector('[id^="productPrice-"]');
      if (!original) return;
      var active = hasCustom || this.getQty() > 1;
      this._mainRoot.classList.toggle('has-camera-configured-price', active);
      // The existing bulk-price skin has high-specificity display rules.
      // Save/restore its inline display rather than leaving two totals visible.
      if (!this._basePriceDisplay) this._basePriceDisplay = new WeakMap();
      var displays = this._basePriceDisplay;
      priceArea.querySelectorAll(':scope > [id^="productPrice-"], :scope > .pdp-volume-hint').forEach(function (element) {
        if (active) {
          if (!displays.has(element)) displays.set(element, { value: element.style.getPropertyValue('display'), priority: element.style.getPropertyPriority('display') });
          element.style.setProperty('display', 'none', 'important');
        } else if (displays.has(element)) {
          var saved = displays.get(element);
          if (saved.value) element.style.setProperty('display', saved.value, saved.priority);
          else element.style.removeProperty('display');
          displays.delete(element);
        }
      });
      if (!this._mainPricing) {
        var region = document.createElement('div');
        region.className = 'pbundle__main-pricing';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        region.innerHTML = '<div class="pbundle__main-price-line"><span class="pbundle__main-amount"></span><span class="pbundle__quantity-label"></span></div>' +
          '<div class="pbundle__main-bulk"><strong class="pbundle__main-bulk-amount"></strong><span class="pbundle__main-bulk-label">Bulk Price</span><span class="pbundle__main-bulk-note"></span></div>' +
          '<p class="pbundle__customer-price-note" hidden></p>';
        priceArea.insertBefore(region, original);
        this._mainPricing = region;
      }
      this._mainPricing.hidden = !active;
      if (!active) return;
      var currency = this.dataset.currency || 'AUD';
      var currencyCode = this.dataset.currencyCodeEnabled === 'true' ? ' ' + currency : '';
      function money(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(cents / 100) + currencyCode;
      }
      this._mainPricing.querySelector('.pbundle__main-amount').textContent = money(total);
      this._mainPricing.querySelector('.pbundle__quantity-label').textContent = this.state.kit ? 'Kit total' : 'Total · ' + this.getQty() + (this.getQty() === 1 ? ' camera' : ' cameras') + (this.state.nvr ? ' + NVR' : '') + (this.state.hdd ? ' + HDD' : '') + (this.monitorSelector && this.monitorSelector.getLines().length ? ' + monitor' : '');
      var bulk = this._mainPricing.querySelector('.pbundle__main-bulk');
      bulk.hidden = !bulkQualifies;
      var amount = bulk.querySelector('.pbundle__main-bulk-amount');
      // Preserve the server's audience / MAP gate; no price is revealed if
      // the existing customizer was only allowed to show the discount percent.
      amount.hidden = !this.bulkPriceEl;
      amount.textContent = this.bulkPriceEl ? money(bulkTotal) : '';
      var percent = Math.round(this.volPercent) + '%';
      bulk.querySelector('.pbundle__main-bulk-note').textContent = this.bulkPriceEl
        ? percent + ' off ' + money(this.volThresholdCents).replace(currencyCode, '').replace(/\.00$/, '') + '+ · Auto at checkout'
        : 'Qualifies for ' + percent + ' off at checkout.';
      var customerNote = this._mainPricing.querySelector('.pbundle__customer-price-note');
      var trade = original.querySelector('.trade-price-block');
      var member = original.querySelector('.member-price-block');
      customerNote.hidden = bulkQualifies || (!trade && !member);
      customerNote.textContent = trade ? 'Eligible Trade discounts are applied at checkout.' : 'Eligible member discounts are applied at checkout.';
    }


    // ---------- sticky-bar bridge ---------------------------------------
    // The theme's sticky bar "Add to cart" forwards to the MAIN product
    // form and would add ONLY the bare product — silently dropping a
    // configured bundle. While the bundle has any selection we relabel the
    // sticky button (with live total) and route its click to this
    // component's proven submit() path (same transport, drawer refresh and
    // cart.js header-count update as the section CTA). With no selection
    // the native behavior is completely untouched. Theme JS is NOT
    // modified: the click is caught in the CAPTURE phase on document,
    // which runs before the sticky button's own listener
    // (SHTProductFormSticky.onClickSubmitButtonHandle).
    _initStickyBridge() {
      this._stickyBtn = document.querySelector('sht-prd-frm-sticky .js-product-form-submit-btn');
      this._stickyTxt = this._stickyBtn ? this._stickyBtn.querySelector('.js-product-form-submit-btn-text') : null;
      this._stickySpinner = document.querySelector('sht-prd-frm-sticky .js-product-form-spinner');
      this._stickyActive = false;
      this._stickyOrig = null;
      if (!this._stickyBtn || !this._stickyTxt || this._inline) return;
      var self = this;
      document.addEventListener('click', function (ev) {
        if (!self._stickyActive) return;
        var btn = ev.target.closest('sht-prd-frm-sticky .js-product-form-submit-btn');
        if (!btn) return;
        ev.preventDefault();
        ev.stopPropagation();
        if (btn.disabled || btn.classList.contains('loading')) return;
        self.submit();
      }, true);
    }

    _syncSticky(active, labelText) {
      if (!this._stickyBtn || !this._stickyTxt) return;
      if (active) {
        if (this._stickyOrig === null) this._stickyOrig = this._stickyTxt.textContent.trim();
        this._stickyTxt.textContent = labelText;
      } else if (this._stickyActive && this._stickyOrig !== null) {
        this._stickyTxt.textContent = this._stickyOrig;
        this._stickyOrig = null;
      }
      this._stickyActive = active;
    }

    _setStickyLoading(loading) {
      if (!this._stickyBtn) return;
      if (loading) {
        this._stickyBtn.classList.add('loading');
        this._stickyBtn.setAttribute('disabled', 'disabled');
        if (this._stickySpinner) this._stickySpinner.classList.remove('hidden');
      } else {
        this._stickyBtn.classList.remove('loading');
        this._stickyBtn.removeAttribute('disabled');
        if (this._stickySpinner) this._stickySpinner.classList.add('hidden');
      }
    }

    bindEvents() {
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      if (this.qtyInput) {
        var handler = this.recompute.bind(this);
        this.qtyInput.addEventListener('change', handler);
        this.qtyInput.addEventListener('input', handler);
      }
    }

    getQty() {
      if (!this.qtyInput) return 1;
      var q = parseInt(this.qtyInput.value, 10);
      return q > 0 ? q : 1;
    }

    onClick(ev) {
      var cta = ev.target.closest('.js-pbundle-add');
      if (cta) {
        ev.preventDefault();
        this.submit();
      }
    }

    onAddonChange() {
      // Picking any add-on (accessory/cable) clears a selected Kit.
      var hasAddon =
        (this.accessorySelector && this.accessorySelector.getLines().length > 0) ||
        (this.cableSelector && this.cableSelector.getLines().length > 0);
      if (hasAddon && this.state.kit) this.clearRow('kit');
      this.recompute();
    }

    onChange(ev) {
      var input = ev.target.closest('.pbundle__opt-input');
      if (!input) return;
      var row = input.dataset.row;
      if (!row) return; // accessory panel inputs have no data-row -> handled by its own controller

      this.setSingle(row, input);
      if (this._inline && row === 'nvr' && this._recorderIncludesHdd()) this.clearRow('hdd');
      if (this._inline) this.hideError();

      // Kit exclusivity
      if (row === 'kit' && input.dataset.handle) {
        // User picked a Kit -> clear NVR / HDD / Accessories / Cable
        this.clearRow('nvr');
        this.clearRow('hdd');
        if (this.accessorySelector) this.accessorySelector.clear();
        if (this.cableSelector) this.cableSelector.clear();
      } else if ((row === 'nvr' || row === 'hdd') && input.dataset.handle) {
        // User picked something else -> clear any Kit selection
        this.clearRow('kit');
      }

      this.recompute();
    }

    setSingle(row, input) {
      var handle = input.dataset.handle || null;
      var variant = handle ? parseInt(input.dataset.variant, 10) : null;
      var price = handle ? parseInt(input.dataset.price, 10) : 0;
      this.state[row] = handle ? { handle: handle, variant: variant, price: price } : null;
      this.repaintRow(row);
    }

    clearRow(row) {
      this.state[row] = null;
      var inputs = this._rowInputs(row);
      inputs.forEach(function (i) { i.checked = i.value === ''; });
      this.repaintRow(row);
    }

    repaintRow(row) {
      var inputs = this._rowInputs(row);
      inputs.forEach(function (input) {
        var label = input.closest('.pbundle__opt');
        if (!label) return;
        label.classList.toggle('is-selected', input.checked);
      });
    }

    // ---------- compute + render ---------------------------------------

    recompute() {
      this._refreshCameraVariant();
      if (window.SmarketCameraOptions) window.SmarketCameraOptions.setVariant(this, this.state.kit ? this.state.kit.variant : this.baseVariantId);
      // Total = (camera × qty) + selected add-ons. NVR / HDD / Accessory stay
      // at qty 1 — typical setup needs one of each regardless of camera count.
      // For Kit path, Kit replaces camera entirely (Kit usually already
      // bundles cameras and an NVR).
      var qty = this.getQty();

      // Cables are 1-per-camera: keep the cable selector's camera count in
      // sync with the main-product quantity so its smart default quantity
      // and "N cables / M cameras" strip work here like on the kit page.
      if (this.cableSelector && typeof this.cableSelector.setCameraCount === 'function') {
        this.cableSelector.setCameraCount(qty);
      }

      var total = this.basePrice * qty;
      var label;
      var stickyActive = false;

      if (this.state.kit) {
        total = this.state.kit.price;
        label = this.ctaKit;
        stickyActive = true;
      } else {
        var accTotal = this.accessorySelector ? this.accessorySelector.getTotal() : 0;
        var accCount = this.accessorySelector ? this.accessorySelector.getLines().length : 0;
        var cableTotal = this.cableSelector ? this.cableSelector.getTotal() : 0;
        var cableCount = this.cableSelector ? this.cableSelector.getLines().length : 0;
        var hasAddOn = this.state.nvr || this.state.hdd || accCount > 0 || cableCount > 0;
        if (this.state.nvr) total += this.state.nvr.price;
        if (this.state.hdd) total += this.state.hdd.price;
        total += accTotal + cableTotal;
        label = hasAddOn ? this.ctaBundle : this.ctaDefault;
        stickyActive = !!hasAddOn;
      }

      var monitorTotal = this.monitorSelector ? this.monitorSelector.getTotal() : 0;
      total += monitorTotal;
      if (monitorTotal) { stickyActive = true; if (!this.state.kit) label = this.ctaBundle; }
      if (this.totalEl) this.totalEl.textContent = formatMoney(total, this.moneyFormat);

      // Bulk Savings price — real payable amount once the configured total
      // crosses the threshold. Element only exists for eligible audiences
      // (non-Trade); the price sub-element is absent for MAP-restricted
      // guests (percent-only copy). Same rounding as the Liquid side.
      var bulkQualifies = false;
      var bulkTotal = total;
      if (this.bulkEl && this.volThresholdCents > 0) {
        bulkQualifies = total >= this.volThresholdCents;
        if (bulkQualifies) {
          bulkTotal = total - Math.round(total * this.volPercent / 100);
          if (this.bulkPriceEl) {
            this.bulkPriceEl.textContent = formatMoney(bulkTotal, this.moneyFormat);
          }
        }
        this.bulkEl.hidden = !bulkQualifies;
      }

      if (this.ctaLabelEl) this.ctaLabelEl.textContent = label;
      var stickyPriceText = (bulkQualifies && this.bulkPriceEl)
        ? formatMoney(bulkTotal, this.moneyFormat) + ' (' + Math.round(this.volPercent) + '% off)'
        : formatMoney(total, this.moneyFormat);
      this._syncSticky(stickyActive, label + ' · ' + stickyPriceText);
      this._syncInline(total, bulkTotal, bulkQualifies, stickyActive);
    }

    // ---------- submit -------------------------------------------------

    buildPayload() {
      this._refreshCameraVariant();
      if (this.state.kit) {
        var kitItem = { id: this.state.kit.variant, quantity: 1 };
        if (window.SmarketCameraOptions) window.SmarketCameraOptions.apply(kitItem);
        return { items: [kitItem].concat(this.monitorSelector ? this.monitorSelector.getCartItems() : []) };
      }
      var qty = this.getQty();
      var cameraItem = { id: this.baseVariantId, quantity: qty };
      if (window.SmarketCameraOptions) window.SmarketCameraOptions.apply(cameraItem);
      var items = [cameraItem];
      if (this.state.nvr) items.push({ id: this.state.nvr.variant, quantity: 1 });
      if (this.state.hdd) items.push({ id: this.state.hdd.variant, quantity: 1 });
      if (this.accessorySelector) {
        this.accessorySelector.getCartItems().forEach(function (it) { items.push(it); });
      }
      if (this.cableSelector) {
        this.cableSelector.getCartItems().forEach(function (it) { items.push(it); });
      }
      if (this.monitorSelector) items = items.concat(this.monitorSelector.getCartItems());
      return { items: items };
    }

    async submit() {
      if (this.ctaEl.classList.contains('is-loading')) return;
      if (this._inline && this._cartNeedsReview) { this.showError('Please review your cart before adding this setup again.'); return; }
      this._refreshCameraVariant();
      if (this._inline && !this._validateInline()) return;
      var cameraOptions = window.SmarketCameraOptions;
      if (cameraOptions && cameraOptions.available && !cameraOptions.available(this.state.kit ? this.state.kit.variant : this.baseVariantId)) {
        this.showError('No lens / colour combination is currently available for this camera.');
        return;
      }

      // ===== Transport aligned with section-product-kit-customizer.js =====
      // Why this shape (matches the theme's native SHTProductForm transport):
      //   • FormData body + `X-Requested-With: XMLHttpRequest` + `Accept:
      //     application/javascript` is the EXACT request shape the merchant
      //     theme's main "Add to cart" button uses. It rides through the
      //     same CDN/WAF/app stack with zero edge surprises.
      //   • Each line item is POSTed individually; only the LAST POST
      //     carries `sections` + `sections_url`, so its response body
      //     contains the rendered drawer/notification HTML reflecting the
      //     full cart (every preceding add already applied).
      //   • Drawer is opened explicitly via window.shtCartDrawer.openDrawer
      //     (the same API the theme's native cart-notification "View" link
      //     uses) — gives the customer a clear visual confirmation, while
      //     skipping the toast notification avoids the "double popup" UX
      //     the customer flagged on the kit customizer.
      this.hideError();
      this.ctaEl.classList.add('is-loading');
      this._setMainLoading(true);
      this.ctaEl.setAttribute('disabled', 'disabled');
      if (this._stickyActive) this._setStickyLoading(true);

      var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var cartAddUrl = (window.routes && window.routes.cart_add_url) || (rootUrl + 'cart/add.js');

      var payload = this.buildPayload();
      var items = (payload && payload.items) || [];

      var cartNotification = document.querySelector('sht-cart-noti');
      var cartDrawer = document.querySelector('sht-cart-drwr');
      var cartDrawerForm = document.querySelector('sht-cart-drwr-frm');

      // Pre-compute the sections list to attach to the last add.
      var sectionsToRender = [];
      if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
        sectionsToRender = sectionsToRender.concat(cartNotification.getSectionsToRender());
      }
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sectionsToRender = sectionsToRender.concat(cartDrawer.getSectionsToRender());
      }
      var sectionsList = Array.from(new Set(
        sectionsToRender.map(function (s) { return s.id; }).filter(Boolean)
      )).join(',');

      console.groupCollapsed('[pbundle] submit', items.length, 'item(s)');
      console.log('  cartAddUrl:', cartAddUrl);
      console.log('  sectionsList:', sectionsList);
      console.log('  items:', JSON.parse(JSON.stringify(items)));

      try {
        var lastResponse = null;

        for (var idx = 0; idx < items.length; idx++) {
          var item = items[idx];
          var isLast = (idx === items.length - 1);

          var fd = new FormData();
          fd.append('id', String(item.id));
          fd.append('quantity', String(item.quantity));
          if (item.properties) {
            Object.keys(item.properties).forEach(function (k) {
              fd.append('properties[' + k + ']', String(item.properties[k]));
            });
          }
          if (isLast && sectionsList) {
            fd.append('sections', sectionsList);
            fd.append('sections_url', window.location.pathname);
          }

          // Echo the encoded payload so DevTools matches what the Network panel shows.
          var dbgPayload = {};
          fd.forEach(function (v, k) { dbgPayload[k] = v; });
          console.log('  POST #' + idx + (isLast ? ' (last, with sections)' : ''), dbgPayload);

          var res = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/javascript'
            },
            body: fd
          });

          var json = null;
          try { json = await res.json(); } catch (e) { /* body may be empty */ }

          console.log('  POST #' + idx + ' response:', res.status, json);

          if (!res.ok || (json && json.status)) {
            var msg = (json && (json.description || json.message))
                      || ('Add to cart failed (HTTP ' + res.status + ')');
            console.error('[pbundle] add.js failed at item ' + idx + ':', res.status, json);
            console.groupEnd();
            if (this._inline && idx > 0) { this._cartNeedsReview = true; msg += ' Some items were already added. Review your cart before adding again.'; }
            if (this._inline && res.status === 429 && idx === 0) msg = 'The cart is busy. Please wait a moment and try again.';
            this.showError(msg);
            return;
          }

          if (isLast) lastResponse = json;
        }

        console.log('  lastResponse has sections?',
                    !!(lastResponse && lastResponse.sections),
                    lastResponse && lastResponse.sections
                      ? Object.keys(lastResponse.sections)
                      : null);

        // Update ONLY the drawer DOM. We deliberately skip
        // cartNotification.renderContents() because its last line is
        // this.open() — which pops a toast. Bundle adds may contain N line
        // items (camera + NVR + HDD + accessories); the drawer is the right
        // surface to show them all in one place, and a redundant toast on
        // top creates a double-popup UX.
        if (lastResponse && cartDrawerForm && typeof cartDrawerForm.renderContents === 'function') {
          try {
            cartDrawerForm.renderContents(lastResponse);
          } catch (e) {
            console.warn('[pbundle] cartDrawerForm.renderContents failed:', e);
          }
        }

        // Open the drawer explicitly (renderContents only updates DOM).
        try {
          if (window.shtCartDrawer && typeof window.shtCartDrawer.openDrawer === 'function') {
            var headerCartBtn = document.querySelector('.js-header-cart-status-btn')
                             || document.querySelector('#headerCartStatus')
                             || document.body;
            window.shtCartDrawer.openDrawer(headerCartBtn);
          }
        } catch (e) {
          console.warn('[pbundle] openDrawer failed:', e);
        }

        // Bubble events for analytics / other listeners.
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Update header cart count from cart.js (source of truth).
        try {
          var cartRes = await fetch(rootUrl + 'cart.js');
          if (cartRes.ok) {
            var cart = await cartRes.json();
            console.log('  post-submit cart.js:',
                        'item_count=' + cart.item_count,
                        'items=' + cart.items.length);
            updateHeaderCartCount(cart.item_count);
          }
        } catch (e) { /* noop */ }

        console.groupEnd();
        if (!this._inline) this.resetSelection();
      } catch (err) {
        console.error('[pbundle] submit threw:', err);
        console.groupEnd();
        if (this._inline) { this._cartNeedsReview = true; this.showError('The cart response was interrupted. Review your cart before adding again.'); }
        else this.showError('Network error — please try again');
      } finally {
        this.ctaEl.classList.remove('is-loading');
        this._setMainLoading(false);
        this.ctaEl.removeAttribute('disabled');
        this._setStickyLoading(false);
      }
    }

    resetSelection() {
      this.state = { nvr: null, hdd: null, kit: null };
      ['nvr', 'hdd', 'kit'].forEach(this.clearRow.bind(this));
      if (this.monitorSelector) this.monitorSelector.clear(false);
      if (this.accessorySelector) this.accessorySelector.clear();
      if (this.cableSelector) this.cableSelector.clear();
      this.recompute();
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
      if (this._inlineError) {
        this._inlineError.textContent = msg; this._inlineError.hidden = false;
        if (this._cartNeedsReview) { var link = document.createElement('a'); link.href = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart'; link.textContent = ' Review cart'; this._inlineError.appendChild(link); }
      }
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
      if (this._inlineError) this._inlineError.hidden = true;
    }
  }

  customElements.define('sht-prd-bundle', SHTProductBundle);
})();
